import { warn } from '@jepto/shared/utils/logger';

import {
	queryGetCardData,
	queryGetCardDataWithoutPagination,
	queryGetCardDataWithoutRequestTracing,
	queryGetCardDataWithoutRequestTracingAndPagination,
} from './get-card-data';

export interface GetCardDataPaginationInput {
	offset: number;
	limit: number;
}

export interface GetCardDataQueryVariables {
	cardId: string;
	timeDimension: Record<string, unknown>;
	accountId?: string | null;
	refresh?: boolean;
	pagination?: GetCardDataPaginationInput;
	/** Partial CardDataConfigInput; `order` must already be AWSJSON string. */
	dataConfig?: { order?: string };
}

export type GetCardDataGraphQLResponse = {
	data?: { getItem?: { cardData?: unknown } };
};

/** True when the API schema has no `requestId` / `requester` on `getItem.cardData`. */
export function isGetCardDataUnknownTracingArgumentError(
	error: unknown
): boolean {
	const message = error instanceof Error ? error.message : String(error);
	return (
		message.includes(
			"Unknown field argument requestId @ 'getItem/cardData'"
		) ||
		message.includes(
			"Unknown field argument requester @ 'getItem/cardData'"
		)
	);
}

/** True when the API schema has no `refresh` on `getItem.cardData`. */
export function isGetCardDataUnknownRefreshArgumentError(
	error: unknown
): boolean {
	const message = error instanceof Error ? error.message : String(error);
	return message.includes(
		"Unknown field argument refresh @ 'getItem/cardData'"
	);
}

/** True when the API schema has no `pagination` on `getItem.cardData`. */
export function isGetCardDataUnknownPaginationArgumentError(
	error: unknown
): boolean {
	const message = error instanceof Error ? error.message : String(error);
	return message.includes(
		"Unknown field argument pagination @ 'getItem/cardData'"
	);
}

/** True when the API schema has no `dataConfig` on `getItem.cardData`. */
export function isGetCardDataUnknownDataConfigArgumentError(
	error: unknown
): boolean {
	const message = error instanceof Error ? error.message : String(error);
	return message.includes(
		"Unknown field argument dataConfig @ 'getItem/cardData'"
	);
}

function selectGetCardDataQuery(opts: {
	includeTracing: boolean;
	includePagination: boolean;
}): string {
	if (opts.includeTracing) {
		return opts.includePagination
			? queryGetCardData
			: queryGetCardDataWithoutPagination;
	}
	return opts.includePagination
		? queryGetCardDataWithoutRequestTracing
		: queryGetCardDataWithoutRequestTracingAndPagination;
}

function buildGetCardDataVariables(
	variables: GetCardDataQueryVariables,
	tracing?: { requestId: string; requester: string },
	opts?: {
		includeRefresh?: boolean;
		includePagination?: boolean;
		includeDataConfig?: boolean;
	}
): Record<string, unknown> {
	const gqlVariables: Record<string, unknown> = {
		cardId: variables.cardId,
		timeDimension: variables.timeDimension,
	};
	const accountId = variables.accountId?.trim();
	if (accountId) {
		gqlVariables.accountId = accountId;
	}
	const includeRefresh = opts?.includeRefresh ?? true;
	const includePagination = opts?.includePagination ?? true;
	if (includeRefresh && variables.refresh === true) {
		gqlVariables.refresh = true;
	}
	if (includePagination && variables.pagination !== undefined) {
		gqlVariables.pagination = {
			offset: Math.max(0, variables.pagination.offset),
			limit: Math.max(1, variables.pagination.limit),
		};
	}
	if (
		opts?.includeDataConfig !== false &&
		variables.dataConfig !== undefined
	) {
		gqlVariables.dataConfig = variables.dataConfig;
	}
	if (tracing) {
		gqlVariables.requestId = tracing.requestId;
		gqlVariables.requester = tracing.requester;
	}
	return gqlVariables;
}

/**
 * GraphQL `accountId` for card data. Cognito Jepto omits it (tenancy is on the
 * token). Public IAM analytics still sends the path-decoded account id.
 */
export function iamOnlyGraphqlAccountId(account?: {
	getAccountId?: () => string | null | undefined;
	getCognitoUserId?: () => string | null | undefined;
} | null): string | undefined {
	const accountId = account?.getAccountId?.()?.trim();
	if (!accountId) {
		return undefined;
	}
	if (account?.getCognitoUserId?.()?.trim()) {
		return undefined;
	}
	return accountId;
}

/**
 * Loads `getItem.cardData`, retrying without tracing args when the schema is older.
 */
export async function fetchGetCardDataItem(
	runQuery: (
		query: string,
		variables: Record<string, unknown>
	) => Promise<unknown>,
	variables: GetCardDataQueryVariables,
	tracing: { requestId: string; requester: string }
): Promise<GetCardDataGraphQLResponse> {
	const runWithOptions = async (opts: {
		includeTracing: boolean;
		includeRefresh: boolean;
		includePagination: boolean;
		includeDataConfig: boolean;
	}): Promise<GetCardDataGraphQLResponse> => {
		const query = selectGetCardDataQuery(opts);
		const vars = buildGetCardDataVariables(
			{
				...variables,
				refresh: opts.includeRefresh ? variables.refresh : undefined,
			},
			opts.includeTracing ? tracing : undefined,
			{
				includeRefresh: opts.includeRefresh,
				includePagination: opts.includePagination,
				includeDataConfig: opts.includeDataConfig,
			}
		);
		return (await runQuery(query, vars)) as GetCardDataGraphQLResponse;
	};

	const runWithDataConfigFallback = async (opts: {
		includeTracing: boolean;
		includeRefresh: boolean;
		includePagination: boolean;
	}): Promise<GetCardDataGraphQLResponse> => {
		if (variables.dataConfig === undefined) {
			return runWithOptions({
				...opts,
				includeDataConfig: false,
			});
		}
		try {
			return await runWithOptions({
				...opts,
				includeDataConfig: true,
			});
		} catch (error) {
			if (!isGetCardDataUnknownDataConfigArgumentError(error)) {
				throw error;
			}
			warn(
				'getCardData: schema rejected dataConfig; retrying without dataConfig'
			);
			return runWithOptions({
				...opts,
				includeDataConfig: false,
			});
		}
	};

	const runWithPaginationFallback = async (opts: {
		includeTracing: boolean;
		includeRefresh: boolean;
	}): Promise<GetCardDataGraphQLResponse> => {
		if (variables.pagination === undefined) {
			return runWithDataConfigFallback({
				...opts,
				includePagination: false,
			});
		}
		try {
			return await runWithDataConfigFallback({
				...opts,
				includePagination: true,
			});
		} catch (error) {
			if (!isGetCardDataUnknownPaginationArgumentError(error)) {
				throw error;
			}
			warn(
				'getCardData: schema rejected pagination argument; retrying without pagination'
			);
			return runWithDataConfigFallback({
				...opts,
				includePagination: false,
			});
		}
	};

	try {
		return await runWithPaginationFallback({
			includeTracing: true,
			includeRefresh: true,
		});
	} catch (error) {
		if (isGetCardDataUnknownRefreshArgumentError(error)) {
			try {
				return await runWithPaginationFallback({
					includeTracing: true,
					includeRefresh: false,
				});
			} catch (retryError) {
				if (!isGetCardDataUnknownTracingArgumentError(retryError)) {
					throw retryError;
				}
				return runWithPaginationFallback({
					includeTracing: false,
					includeRefresh: false,
				});
			}
		}
		if (!isGetCardDataUnknownTracingArgumentError(error)) {
			throw error;
		}
		try {
			return await runWithPaginationFallback({
				includeTracing: false,
				includeRefresh: variables.refresh === true,
			});
		} catch (legacyError) {
			if (
				variables.refresh === true &&
				isGetCardDataUnknownRefreshArgumentError(legacyError)
			) {
				return runWithPaginationFallback({
					includeTracing: false,
					includeRefresh: false,
				});
			}
			throw legacyError;
		}
	}
}
